"use client";

import React, { useState, useEffect, useRef } from "react";
import { RefreshCw } from "lucide-react";
import { getShuffledRecommendedQuestions, type QuestionItem } from "@/lib/recommended-questions";
import { useLanguage } from "@/contexts/language-context";

interface RecommendedQuestionsProps {
  onQuestionClick: (fullQuery: string) => void;
}

export default function RecommendedQuestions({ onQuestionClick }: RecommendedQuestionsProps) {
  const { t, language } = useLanguage();
  // Initialize with null to avoid hydration mismatch
  const [shuffledQuestions, setShuffledQuestions] = useState<QuestionItem[] | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const requestIdRef = useRef(0);

  // Fetch questions after component mounts on client or when language changes
  useEffect(() => {
    loadQuestions(language);
  }, [language]);

  /**
   * Load questions from CloudFront (with cache) or fallback for current language
   */
  async function loadQuestions(activeLanguage: typeof language, forceRefresh = false) {
    const requestId = ++requestIdRef.current;
    try {
      const questions = await getShuffledRecommendedQuestions(forceRefresh, activeLanguage);
      if (requestId === requestIdRef.current) {
        setShuffledQuestions(questions);
      }
    } catch (error) {
      console.error('[RecommendedQuestions] Error loading questions:', error);
      // Component will remain in loading state (null) if this fails
    }
  }

  /**
   * Handle refresh button click
   * If cache is valid, will get new random 4 from cached pool
   * If cache expired, will fetch fresh data from CloudFront
   */
  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await loadQuestions(language, false); // Don't force refresh - use cache if available
    } finally {
      // Add small delay for visual feedback
      setTimeout(() => setIsRefreshing(false), 300);
    }
  }

  // Don't render until questions are loaded (client-side only)
  if (!shuffledQuestions) {
    return null;
  }

  return (
    <div className="w-full max-w-3xl mx-auto mb-6 animate-fade-in">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center mb-3 px-1">
        <div className="text-sm text-[#666] font-medium">
          {t('questions.suggested')}
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="group flex items-center gap-1.5 text-xs cursor-pointer text-[#666] hover:text-gray-700 disabled:text-gray-400 transition-colors duration-200 px-2 py-1 rounded-md hover:bg-gray-50 disabled:bg-transparent disabled:cursor-not-allowed"
          aria-label={t('questions.refresh')}
        >
          <RefreshCw
            className={`h-3.5 w-3.5 transition-transform duration-300 ${
              isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'
            }`}
          />
          <span className="hidden sm:inline">
            {isRefreshing ? t('questions.refreshing') : t('questions.refreshButton')}
          </span>
        </button>
      </div>

      {/* Questions grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {shuffledQuestions.map((question, index) => {
          return (
            <button
              key={`${question.displaySummary}-${index}`}
              onClick={() => onQuestionClick(question.fullQuery)}
              className="group relative bg-[#231f20] hover:bg-[#2a2627] text-[#ccc] rounded-xl shadow-sm px-5 py-3.5 cursor-pointer transition-all duration-200 hover:shadow-md text-left"
            >
              <span className="text-sm font-normal truncate block">
                {question.displaySummary}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
